import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Card, Input, Space, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { productApi } from '../../../api';

/**
 * Admin surface — Compliance / Exports.
 * Central page linking to every CSV export. Each button downloads via a
 * direct <a href> to `/api/product/institutions/:slug/exports/...`.
 */
export function CompliancePage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const today = new Date().toISOString().slice(0, 10);
  const [receiptsFrom, setReceiptsFrom] = useState(today);
  const [receiptsTo, setReceiptsTo] = useState(today);
  const now = new Date();
  const [payrollMonth, setPayrollMonth] = useState(`${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`);
  const [attendanceDate, setAttendanceDate] = useState(today);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Compliance &amp; exports</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          Every export writes an entry to the audit log with the caller's email and row count.
        </Typography.Text>
      </div>

      <Alert type="info" showIcon message="CSV downloads open in a new tab and stream directly. Nothing is stored on your device beyond the file." />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
        <ExportCard
          title="Students"
          description="Full student list with admission number, gender, DOB, grade, section, status."
          href={productApi.exportUrl(institution, 'students')}
        />
        <ExportCard
          title="Bills (vendor invoices)"
          description="All vendor bills with vendor, number, amount, paid, due, status."
          href={productApi.exportUrl(institution, 'bills')}
        />
        <ExportCard
          title="Receipts (fee collection)"
          description="Filter by date range. Receipt number + student + head + amount + method."
          href={productApi.exportUrl(institution, 'receipts', { from: receiptsFrom, to: receiptsTo })}
          controls={
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <Input size="small" value={receiptsFrom} onChange={(e) => setReceiptsFrom(e.target.value)} placeholder="from" style={{ width: 130, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
              <Input size="small" value={receiptsTo} onChange={(e) => setReceiptsTo(e.target.value)} placeholder="to" style={{ width: 130, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
            </div>
          }
        />
        <ExportCard
          title="Payslips"
          description="Payroll run for a month. Basic + allowances + deductions + net per staff."
          href={productApi.exportUrl(institution, 'payslips', { month: payrollMonth })}
          controls={
            <div style={{ marginBottom: 10 }}>
              <Input size="small" value={payrollMonth} onChange={(e) => setPayrollMonth(e.target.value)} placeholder="yyyy-mm" style={{ width: 130, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
            </div>
          }
        />
        <ExportCard
          title="Attendance (per day)"
          description="One row per student marked on the chosen day. Status + note."
          href={productApi.exportUrl(institution, 'attendance', { date: attendanceDate })}
          controls={
            <div style={{ marginBottom: 10 }}>
              <Input size="small" value={attendanceDate} onChange={(e) => setAttendanceDate(e.target.value)} placeholder="yyyy-mm-dd" style={{ width: 130, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
            </div>
          }
        />
      </div>
    </Space>
  );
}

function ExportCard({
  title, description, href, controls,
}: { title: string; description: string; href: string; controls?: React.ReactNode }) {
  return (
    <Card size="small" title={title}>
      <Typography.Paragraph type="secondary" style={{ margin: '0 0 12px', fontSize: 12.5 }}>
        {description}
      </Typography.Paragraph>
      {controls}
      <Button type="primary" icon={<DownloadOutlined />} href={href} target="_blank" rel="noopener">
        Download CSV
      </Button>
    </Card>
  );
}
